import { useAppDispatch } from '../../../../redux/hooks';
import { setSingleDayLabelAction } from '../../../../redux/slices/analyses';
import {
  ExpressionModeKey,
  QueryType,
  TimeUnit,
} from '../../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../../utils/types';
import SingleSelectMenu from '../../../Dropdown/SingleSelectMenu';
import { useChartContext } from '../../ChartContext';
import ChartTypeSelector from '../../ChartTypeSelector';
import { CHART_TYPES } from '../../constants/chartTypes';
import * as ChartControlBarUI from '../ChartControlBarUI';
import ChartNumberTypeSelector from '../ChartNumberTypeSelector';
import ExpandedControlBar from '../ExpandedControlBar';
import GeoMapControls from '../GeoMapControls';
import ExpressionMeasureSettings from './ExpressionMeasureSettings';

interface IProps {
  analysisId: string;
}

const ExpressionControlBar = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const {
    metadata: { timeUnit, query },
    chartData,
    settings: { chartSettings },
  } = useChartContext();
  const { singleDayLabel, chartType } = chartSettings;
  assertCondition(chartData?.queryType === QueryType.Expression);
  const isHistogramMode =
    query?.__typename === 'ExpressionQuery' &&
    query.expressionMode.key === ExpressionModeKey.Histogram;
  const shouldShowMeasureLineType =
    !isHistogramMode &&
    (chartType === CHART_TYPES.BAR_CHART ||
      chartType === CHART_TYPES.METRIC_CHART ||
      chartType === CHART_TYPES.LINE_CHART);

  const changeSelectedDate = (selectedDate: string) => {
    dispatch(
      setSingleDayLabelAction({
        analysisId,
        singleDayLabel: selectedDate,
      }),
    );
  };

  // Legacy Chart Type - Expanded Bar Chart = BarChart + SingleDayLabel, Planned to remove in the future
  if (singleDayLabel && chartType === CHART_TYPES.BAR_CHART) {
    return <ExpandedControlBar analysisId={analysisId} />;
  }

  const showDateMenu =
    chartType === CHART_TYPES.PIE_CHART ||
    chartType === CHART_TYPES.METRIC_CHART ||
    (chartType === CHART_TYPES.GEO_MAP && timeUnit !== TimeUnit.AllTime);

  return (
    <ChartControlBarUI.ControlBarRow>
      <ChartTypeSelector />
      {showDateMenu && (
        <SingleSelectMenu
          options={chartData.dates
            .map((date) => ({
              value: date,
              text: date,
            }))
            .reverse()}
          value={singleDayLabel}
          onChange={changeSelectedDate}
        />
      )}
      {(chartType === CHART_TYPES.STACKED_COLUMN_CHART ||
        chartType === CHART_TYPES.PERCENT_STACKED_AREA_CHART) && (
        <ChartNumberTypeSelector />
      )}
      {chartType === CHART_TYPES.GEO_MAP && (
        <GeoMapControls analysisId={analysisId} />
      )}
      {shouldShowMeasureLineType && (
        <ExpressionMeasureSettings analysisId={analysisId} />
      )}
    </ChartControlBarUI.ControlBarRow>
  );
};

export default ExpressionControlBar;
